import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCircle, Phone, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HandlerLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('DWS Vault Chat');

  // Modal reset states
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetInput, setResetInput] = useState('');
  const [requestingReset, setRequestingReset] = useState(false);

  const { login, user } = useAuth();

  const handleSendResetRequest = async () => {
    if (!resetInput.trim()) {
      toast.error('Please enter your username or mobile number');
      return;
    }
    try {
      setRequestingReset(true);
      const res = await axios.post(`${API}/auth/request-password-reset`, {
        username_or_phone: resetInput.trim()
      });
      toast.success(res.data?.message || 'Password reset request sent to Admin!');
      setShowResetModal(false);
      setResetInput('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit reset request');
    } finally {
      setRequestingReset(false);
    }
  };

  // Fetch app name
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Handler Login`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    fetchAppName();
  }, []);

  const navigate = useNavigate();

  // Auto-redirect only if already logged in AS HANDLER (matching role)
  // If user is logged in as different role, allow them to see this page and log in as handler
  useEffect(() => {
    if (user && ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler'].includes(user.role)) {
      navigate('/handler-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        phone: username,  // Send username in phone field for backend compatibility
        password
      });

      const userData = response.data;
      
      // STRICT ROLE CHECK: Only allow handlers on this page
      if (!['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler'].includes(userData.role)) {
        setError('This is Handler login page. Please use the correct login page for your role.');
        setLoading(false);
        return;
      }

      login(userData);
      
      // Track activity for login
      try {
        await axios.post(`${API}/admin/activity/track`, {
          user_id: userData.id,
          action: 'login'
        });
      } catch (trackErr) {
        console.error('Activity tracking failed:', trackErr);
      }
      
      navigate('/handler-dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/icons/customer/icon-128x128.png" 
              alt="DWS Vault Chat"
              className="w-28 h-28 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">{appName}</h1>
          <p className="text-gray-600 mt-2">Handler Login</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username or Number
            </label>
            <div className="relative">
              <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.slice(0, 10))}
                maxLength={10}
                placeholder="Enter username or number"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                data-testid="handler-username-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          {error && (
            <div className="bg-wa-hover border border-red-200 text-wa-text-primary px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="text-center pt-4 border-t border-gray-100 mt-4">
          <button
            type="button"
            onClick={() => { setResetInput(username); setShowResetModal(true); }}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer hover:underline"
          >
            Forgot Password? Send request to Admin
          </button>
        </div>
      </div>

      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-4 font-sans border border-gray-200">
            <h3 className="text-base font-bold text-gray-900">Request Password Reset</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Enter your username or registered mobile number to send a reset request to the Admin.
            </p>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Username or Number</label>
              <input
                type="text"
                value={resetInput}
                onChange={(e) => setResetInput(e.target.value)}
                placeholder="e.g. 9000000106 or deposit_handler_1"
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendResetRequest}
                disabled={requestingReset}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                {requestingReset ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandlerLogin;